import { ActionDisplayType, ActionHeader } from '@decisions/server/DecisionsFramework/ServiceLayer/Actions';
import { IActionLinkItem, IActionLinkItems, IActionLinksPair } from 'components/ActionLinks/ActionLinkModels';
import { IActionBarItemData } from './ActionsBarItem';
import { isNoneDisplayType, isPrimary } from 'utils/ActionUtils';

/**
 * Get actions bar items from Action Links data.
 *
 * This is downstream of getActionLinksForActions because that already gets the categories/groups for us,
 * and it's advantageous for rendering actions later.
 *
 * @param actionLinks
 * @returns action bar button data
 */
export function getActionBarItemsFromHeaders(
  { originalActionsById, actionLinks }: IActionLinksPair,
  primaryMin = 0,
  secondaryMin = 0,
  showAllButton = true
): IActionBarItemData[] {
  if (!actionLinks.current?.length && !actionLinks.nested?.size) {
    return [];
  }
  const primaryData: IActionBarItemData[] = [];
  const secondaryData: IActionBarItemData[] = [];
  const primaryGroups: IActionBarItemData[] = [];
  const secondaryGroups: IActionBarItemData[] = [];
  const noneActions: IActionBarItemData[] = [];

  actionLinks.current!.forEach(link => {
    const h = originalActionsById.get(link.value) as ActionHeader;
    if (isNoneDisplayType(h)) {
      noneActions.push(
        createActionBarItemFromHeader(h, { priority: secondaryButtonPriority, isPrimary: false, isGroup: false })
      );
    } else if (isPrimary(h)) {
      primaryData.push(
        createActionBarItemFromHeader(h, { priority: primaryButtonPriority, isPrimary: true, isGroup: false })
      );
    } else {
      secondaryData.push(
        createActionBarItemFromHeader(h, { priority: secondaryButtonPriority, isPrimary: false, isGroup: false })
      );
    }
  });

  actionLinks.nested?.forEach((links, key) => {
    const item: IActionBarItemData = {
      label: key,
      id: key, // I bet this could be `${folderId}-${pageName}-all` if it needs to be unique
      // buttons,
      // priority: secondaryButtonPriority,
      isGroup: true,
      isPrimary: false,
      order: getOrder(links.current),
    };

    if (allOfDisplayType(ActionDisplayType.None, links)) {
      noneActions.push(item);
    } else if (anyOfDisplayType(ActionDisplayType.Primary, links)) {
      item.priority = primaryButtonPriority;
      item.isPrimary = true;
      primaryGroups.push(item);
    } else {
      // groups get pushed to secondary if not primary or none.
      item.priority = secondaryButtonPriority;
      secondaryGroups.push(item);
    }
  });

  // respect minimums:
  while (primaryData.length < primaryMin && noneActions.length) {
    const none = noneActions.shift();
    none && primaryData.push(none);
  }

  while (secondaryData.length < secondaryMin && noneActions.length) {
    const none = noneActions.shift();
    none && secondaryData.push(none);
  }

  // these get their "order" actions inside them and are sorted only by "order"
  // which is different than normal sorting done to actions inside of menus,
  // because groups themselves are sorted by order, then name.
  // order is derivative of the lowest order within a group
  primaryGroups.sort((a, b) => a.order - b.order);
  secondaryGroups.sort((a, b) => a.order - b.order);

  const items = [...primaryData, ...primaryGroups, ...secondaryData, ...secondaryGroups];

  return showAllButton
    ? [
        ...items,
        {
          label: 'All',
          id: 'All', // I bet this could be `${folderId}-${pageName}-all` if it needs to be unique
          // buttons,
          priority: allButtonPriority,
          isGroup: true,
          isPrimary: false,
          order: Number.MAX_VALUE,
        },
      ]
    : items;
}

const secondaryButtonPriority = 0;
const primaryButtonPriority = 1;
const allButtonPriority = 2;

export function createActionBarItemFromHeader(
  header: ActionHeader,
  options: {
    priority: number;
    isPrimary: boolean;
    isGroup: boolean;
  }
): IActionBarItemData {
  return {
    ...options,
    ...{ id: header.Id, label: header.Nm, order: header.Or },
  };
}

export function createActionBarButton(options: {
  label: string;
  // buttons: ActionHeader[],
  priority: number;
  isPrimary: boolean;
  isGroup: boolean;
  order: number;
  id: string;
}): IActionBarItemData {
  return {
    ...options,
  };
}

function getOrder(items?: IActionLinkItem[]) {
  if (!items || !items.length) {
    return 0;
  }
  return Math.min(...items.map(item => item.order || 0));
}

function anyOfDisplayType(displayType: ActionDisplayType, items?: IActionLinkItems) {
  if (!items) {
    return false;
  }

  for (let item of items.current ?? []) {
    if (item.displayType === displayType) {
      return true;
    }
  }

  // check nested values recursively, too in case a group only has groups as children:
  // this _appeared_ to check only one level in legacy code, but sub-categories were not yet sorted out in that impl.
  if (items.nested) {
    for (let nested of Array.from(items.nested.values())) {
      if (anyOfDisplayType(displayType, nested)) {
        return true;
      }
    }
  }
  return false;
}

export function allOfDisplayType(displayType: ActionDisplayType, items?: IActionLinkItems) {
  if (!items) {
    return false;
  }

  for (let item of items.current ?? []) {
    if (item.displayType !== displayType) {
      return false;
    }
  }

  // check nested values recursively, too in case a group only has groups as children:
  if (items.nested) {
    for (let nested of Array.from(items.nested.values())) {
      if (!allOfDisplayType(displayType, nested)) {
        return false;
      }
    }
  }

  return true;
}
